import { useState } from 'react';
import { dateVersIso, isoVersDate } from '../api/mappers';
import { tr } from '../lib/i18n';
import { MOYENS_PAIEMENT as MOYENS, textePaye } from '../lib/paye-le';
import { C } from '../theme/palette';

/**
 * Le jour où une facture de fournisseur ou de sous-traitant a été payée, et
 * comment : saisi à la main tant que la banque n'est pas reliée, puis trouvé
 * dans le relevé. Payée, elle le dit ; sinon, un bouton l'ouvre.
 *
 * @param {object} props propriétés du composant
 * @param {string} [props.payeLe] jour du paiement, « JJ.MM.AAAA »
 * @param {string} [props.payePar] « banque », « carte » ou « especes »
 * @param {Function} props.onChange reçoit `{payeLe, payePar}` ; `payeLe` vide efface le paiement
 * @param {string} [props.source] d'où vient le jour (« relevé bancaire », « noté à
 *   la main »), dit à la place du moyen de paiement
 * @returns {JSX.Element} la ligne « Payé le »
 */
export function PayeLe({ payeLe, payePar, onChange, source }) {
  const [ouvert, setOuvert] = useState(false);
  const [jour, setJour] = useState('');
  const [moyen, setMoyen] = useState('banque');

  const ouvre = () => {
    setJour(dateVersIso(payeLe) || new Date().toISOString().slice(0, 10));
    setMoyen(payePar || 'banque');
    setOuvert(true);
  };

  if (!ouvert) {
    return payeLe ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, marginTop: 4 }}>
        <span style={{ color: C.green, fontWeight: 800 }}>
          ✓ {source ? textePaye(payeLe) + ' · ' + source : textePaye(payeLe, payePar)}
        </span>
        <button
          type="button"
          onClick={ouvre}
          aria-label={tr('Changer la date de paiement')}
          style={{
            background: 'none',
            border: 'none',
            color: C.muted,
            cursor: 'pointer',
            fontSize: 10,
          }}
        >
          ✎
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={ouvre}
        style={{
          marginTop: 4,
          background: 'none',
          border: '1px dashed ' + C.border,
          borderRadius: 7,
          padding: '3px 8px',
          fontSize: 9.5,
          fontWeight: 700,
          color: C.muted,
          cursor: 'pointer',
        }}
      >
        ＋ {tr('Payé le…')}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={tr('Payé le')}
      style={{
        marginTop: 6,
        border: '1px solid ' + C.border,
        borderRadius: 9,
        padding: 8,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
      }}
    >
      <label style={{ fontSize: 9.5, fontWeight: 800, color: C.muted }}>
        {tr('Payé le')}{' '}
        <input
          type="date"
          value={jour}
          onChange={(e) => setJour(e.target.value)}
          style={{
            fontSize: 11,
            padding: '4px 6px',
            borderRadius: 7,
            border: '1px solid ' + C.border,
          }}
        />
      </label>
      {MOYENS.map(({ k, lib }) => (
        <button
          key={k}
          type="button"
          aria-pressed={moyen === k}
          onClick={() => setMoyen(k)}
          style={{
            padding: '4px 8px',
            borderRadius: 7,
            fontSize: 9.5,
            fontWeight: 700,
            cursor: 'pointer',
            border: '1px solid ' + (moyen === k ? C.navy : C.border),
            background: moyen === k ? C.navy : C.white,
            color: moyen === k ? '#fff' : C.ink,
          }}
        >
          {tr(lib)}
        </button>
      ))}
      <button
        type="button"
        disabled={!jour}
        onClick={() => {
          onChange({ payeLe: isoVersDate(jour), payePar: moyen });
          setOuvert(false);
        }}
        style={{
          padding: '4px 10px',
          borderRadius: 7,
          fontSize: 9.5,
          fontWeight: 800,
          cursor: 'pointer',
          border: 'none',
          background: C.green,
          color: '#fff',
        }}
      >
        ✓ {tr('OK')}
      </button>
      {payeLe && (
        <button
          type="button"
          onClick={() => {
            onChange({ payeLe: '', payePar: '' });
            setOuvert(false);
          }}
          style={{
            padding: '4px 8px',
            borderRadius: 7,
            fontSize: 9.5,
            fontWeight: 700,
            cursor: 'pointer',
            border: '1px solid ' + C.border,
            background: C.white,
            color: C.red,
          }}
        >
          {tr('Pas encore payé')}
        </button>
      )}
      <button
        type="button"
        onClick={() => setOuvert(false)}
        aria-label={tr('Annuler')}
        style={{
          background: 'none',
          border: 'none',
          color: C.muted,
          cursor: 'pointer',
          fontSize: 11,
        }}
      >
        ✕
      </button>
    </div>
  );
}
